// IM-EXT-014 (IM-UI-070): Configurações > Integrações. Cartão por provedor (primeiro: CERES CRM) com estado simples,
// última sincronização, itens enviados e com falha, testar conexão e desconectar. Detalhe técnico só no "Histórico
// técnico", recolhido. Sem integração, nada muda no restante do produto.
// IM-EXT-018-C01: o ambiente de validação (simulador) é inconfundível — estado próprio, aviso permanente de que nada vai
// ao CERES CRM, entregas simuladas contadas à parte e transporte (SIMULADOR/HTTP) no histórico técnico.
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { CatalogItemDto, CatalogListDto, CreateIntegrationConnectionResponse, IntegrationSummaryDto } from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { Modal } from '../components/Modal';
import { AsyncView } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead, StatusPill } from '../components/Widgets';
import { DELIVERY_LABEL, fmtDateTime, healthOf, integrationsAdapter } from '../data/integrations';
import { useResource } from '../hooks/useResource';

const msg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Não foi possível concluir.');

export function Integrations() {
  const { state, reload, refresh } = useResource((s) => integrationsAdapter.list(s).then((r) => r.items), []);
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Configurações" title="Integrações" subtitle="Conecte a InteliMove a outros sistemas da sua operação comercial. Tudo continua funcionando sem integração."
        actions={<Link className="secondary btn-link" to="/configuracoes">Voltar às configurações</Link>} />
      <AsyncView state={state} onRetry={reload} empty={<p className="card-sub">Nenhuma integração disponível.</p>}>
        {(items) => <div className="settings-grid">{items.map((s) => <IntegrationCard key={s.provider.key} summary={s} onChanged={refresh} />)}</div>}
      </AsyncView>
    </section>
  );
}

export function IntegrationCard({ summary, onChanged }: { summary: IntegrationSummaryDto; onChanged: () => void }) {
  const toast = useToast();
  const { provider, connection, health, canManage } = summary;
  const [busy, setBusy] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const h = healthOf(health);
  const slug = provider.slug;

  // Enquanto há itens na fila, revalida em silêncio (o envio acontece em segundo plano).
  useEffect(() => {
    if (!health.pending) return;
    const t = window.setTimeout(onChanged, 5000);
    return () => window.clearTimeout(t);
  }, [health.pending, health.lastSyncAt, onChanged]);

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try { await fn(); toast(ok); onChanged(); } catch (e) { toast(msg(e), 'error'); } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy('test');
    try { const r = await integrationsAdapter.test(slug); toast(r.message, r.ok ? undefined : 'error'); onChanged(); } catch (e) { toast(msg(e), 'error'); } finally { setBusy(null); }
  };
  const simulated = health.mode === 'SIMULATED';

  return (
    <article className="card integration-card" aria-labelledby={`int-${slug}`}>
      <div className="card-head">
        <div><h2 id={`int-${slug}`}>{provider.name}</h2><div className="card-sub">{provider.description}</div></div>
        <StatusPill tone={h.tone}>{h.label}</StatusPill>
      </div>
      {simulated && <p className="notice-inline integration-sim" role="note"><b>Ambiente de validação.</b> O simulador responde como o CERES CRM responderia, para conferir o contrato. <b>Nenhum dado é enviado ao CERES CRM</b> e nada aqui é uma sincronização real.</p>}
      <p className="card-sub" role="status">{health.summary}</p>
      {connection && (simulated ? (
        <dl className="kv">
          <dt>Entregas simuladas (não enviadas)</dt><dd>{health.simulatedSent}</dd>
          <dt>Itens enviados ao CERES CRM</dt><dd>0 — sem conexão real</dd>
          <dt>Recusadas pelo simulador</dt><dd>{health.failed}</dd>
          {health.pending > 0 && <><dt>Aguardando o simulador</dt><dd>{health.pending}</dd></>}
        </dl>
      ) : (
        <dl className="kv">
          <dt>Última sincronização</dt><dd>{fmtDateTime(health.lastSyncAt)}</dd>
          <dt>Itens enviados ao CERES CRM</dt><dd>{health.sent}</dd>
          <dt>Itens com falha</dt><dd>{health.failed}</dd>
          {health.pending > 0 && <><dt>Aguardando envio</dt><dd>{health.pending}</dd></>}
        </dl>
      ))}
      {canManage ? (
        <div className="form-actions">
          {!connection && <button type="button" className="cta" onClick={() => setConnecting(true)}>Conectar</button>}
          {connection && <button type="button" className="secondary" disabled={busy !== null} onClick={() => void test()}>{busy === 'test' ? 'Testando…' : simulated ? 'Testar no simulador' : 'Testar conexão'}</button>}
          {connection && connection.status !== 'ACTIVE' && (
            <button type="button" className="cta" disabled={busy !== null || (connection.status === 'PENDING' && connection.lastTestOk !== true)} title={connection.status === 'PENDING' && connection.lastTestOk !== true ? 'Teste a conexão antes de ativar' : undefined}
              onClick={() => run('activate', () => integrationsAdapter.activate(slug), simulated ? 'Ambiente de validação ativado (nada é enviado ao CERES CRM).' : 'Sincronização ativada.')}>{connection.status === 'DEACTIVATED' ? 'Retomar' : simulated ? 'Ativar validação' : 'Ativar'}</button>
          )}
          {connection?.status === 'ACTIVE' && <button type="button" className="secondary" disabled={busy !== null} onClick={() => run('pause', () => integrationsAdapter.deactivate(slug), 'Sincronização pausada.')}>Pausar</button>}
          {connection && <button type="button" className="link-btn" disabled={busy !== null} onClick={() => setDisconnecting(true)}>Desconectar</button>}
        </div>
      ) : <p className="muted small">Somente administradores podem alterar integrações.</p>}
      {connection !== null && <CatalogSection slug={slug} name={provider.name} canManage={canManage} />}
      {canManage && connection !== null && (
        <details className="details" onToggle={(e) => setHistoryOpen((e.target as HTMLDetailsElement).open)}>
          <summary>Histórico técnico</summary>
          {historyOpen && <TechHistory slug={slug} onChanged={onChanged} />}
        </details>
      )}
      {connecting && <ConnectDialog summary={summary} onClose={() => setConnecting(false)} onDone={() => { setConnecting(false); onChanged(); }} />}
      {disconnecting && (
        <Modal title={`Desconectar ${provider.name}?`} role="alertdialog" onClose={() => setDisconnecting(false)}>
          <p>As credenciais desta conexão são revogadas e os envios pendentes são cancelados. Nada é apagado: o histórico de sincronização e os dados da InteliMove continuam disponíveis.</p>
          <div className="form-actions">
            <button type="button" className="secondary" onClick={() => setDisconnecting(false)}>Cancelar</button>
            <button type="button" className="cta" disabled={busy !== null} onClick={() => run('revoke', () => integrationsAdapter.revoke(slug), `${provider.name} desconectado.`).then(() => setDisconnecting(false))}>Desconectar</button>
          </div>
        </Modal>
      )}
    </article>
  );
}

function ConnectDialog({ summary, onClose, onDone }: { summary: IntegrationSummaryDto; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const { provider } = summary;
  const [mode, setMode] = useState<'SIMULATOR' | 'HTTP'>('SIMULATOR');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateIntegrationConnectionResponse | null>(null);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null);
    const f = new FormData(e.currentTarget);
    const text = (k: string) => String(f.get(k) ?? '').trim() || undefined;
    try {
      const out = await integrationsAdapter.connect(provider.slug, mode === 'HTTP'
        ? { transport: 'HTTP', endpointUrl: text('endpointUrl'), externalOrgId: text('externalOrgId'), externalOrgName: text('externalOrgName') }
        : { transport: 'SIMULATOR' });
      if (out.secrets) { setCreated(out); return; }
      // Ambiente de validação: conecta, testa e ativa em sequência.
      const t = await integrationsAdapter.test(provider.slug);
      if (!t.ok) { toast(t.message, 'error'); onDone(); return; }
      await integrationsAdapter.activate(provider.slug);
      toast(`Ambiente de validação do ${provider.name} ativado: nenhum dado é enviado ao ${provider.name}.`);
      onDone();
    } catch (err) { setError(msg(err)); } finally { setBusy(false); }
  };

  if (created?.secrets) {
    return (
      <Modal title="Configure o CERES CRM" onClose={onDone} wide>
        <p>Copie estes dados para a configuração de integração do {provider.name}. Por segurança, as chaves <b>não serão mostradas de novo</b>; se perder, desconecte e conecte outra vez.</p>
        <dl className="kv small">
          <dt>Endereço para o {provider.name} enviar eventos</dt><dd><code>{created.secrets.inboundUrl}</code></dd>
          <dt>Chave para validar o que a InteliMove envia</dt><dd><code>{created.secrets.outbound}</code></dd>
          <dt>Chave para assinar o que o {provider.name} envia</dt><dd><code>{created.secrets.inbound}</code></dd>
        </dl>
        <div className="form-actions"><button type="button" className="cta" onClick={onDone} data-autofocus>Já copiei</button></div>
      </Modal>
    );
  }
  return (
    <Modal title={`Conectar ${provider.name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="check-grid" role="radiogroup" aria-label="Forma de conexão">
          <label className="check"><input type="radio" name="mode" checked={mode === 'SIMULATOR'} onChange={() => setMode('SIMULATOR')} />
            <span><b>Ambiente de validação</b><br /><span className="muted small">Confere a integração sem enviar dados a um {provider.name} real.</span></span></label>
          <label className="check"><input type="radio" name="mode" checked={mode === 'HTTP'} onChange={() => setMode('HTTP')} disabled={!provider.httpAvailable} />
            <span><b>{provider.name}</b><br /><span className="muted small">{provider.httpAvailable ? 'Conexão direta com a sua conta.' : 'Disponível quando a API de integração do CERES CRM for publicada.'}</span></span></label>
        </div>
        {mode === 'HTTP' && (
          <div className="form-grid">
            <div className="field wide"><label htmlFor="int-url">Endereço de integração do {provider.name}</label><input id="int-url" name="endpointUrl" type="url" required placeholder="https://" /></div>
            <div className="field"><label htmlFor="int-org">Identificador da organização no {provider.name}</label><input id="int-org" name="externalOrgId" required maxLength={200} /></div>
            <div className="field"><label htmlFor="int-org-name">Nome da organização (opcional)</label><input id="int-org-name" name="externalOrgName" maxLength={200} /></div>
          </div>
        )}
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="form-actions">
          <button type="button" className="secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="cta" disabled={busy}>{busy ? 'Conectando…' : 'Conectar'}</button>
        </div>
      </form>
    </Modal>
  );
}

function TechHistory({ slug, onChanged }: { slug: string; onChanged: () => void }) {
  const toast = useToast();
  const { state, reload } = useResource(async (s) => {
    const [h, d] = await Promise.all([integrationsAdapter.history(slug, s), integrationsAdapter.deliveries(slug, s)]);
    return { history: h.items, deliveries: d.items };
  }, [slug]);
  const retry = async (id: string) => {
    try { await integrationsAdapter.retry(slug, id); toast('Reenvio programado.'); reload(); onChanged(); } catch (e) { toast(msg(e), 'error'); }
  };
  return (
    <AsyncView state={state} onRetry={reload} empty={<p className="muted small">Sem registros.</p>}>
      {({ history, deliveries }) => (
        <div className="tech-history">
          <h3 className="card-title">Entregas recentes</h3>
          {deliveries.length === 0 ? <p className="muted small">Nenhuma entrega ainda.</p> : (
            <div className="table-scroll"><table className="data-table" style={{ minWidth: 0 }}>
              <caption className="sr-only">Entregas recentes</caption>
              <thead><tr><th scope="col">Evento</th><th scope="col">Transporte</th><th scope="col">Situação</th><th scope="col">Tentativas</th><th scope="col">Criada em</th><th scope="col"><span className="sr-only">Ações</span></th></tr></thead>
              <tbody>{deliveries.map((d) => (
                <tr key={d.id}>
                  <td><code>{d.eventType}</code>{d.lastError && <div className="cell-sub">{d.lastError}</div>}</td>
                  <td>{d.transport === 'SIMULATOR' ? 'Simulador (não enviado)' : 'HTTP (real)'}</td>
                  <td>{d.transport === 'SIMULATOR' && d.status === 'DELIVERED' ? 'Respondida pelo simulador' : DELIVERY_LABEL[d.status]}</td>
                  <td>{d.attempts}/{d.maxAttempts}</td>
                  <td>{fmtDateTime(d.createdAt)}</td>
                  <td>{(d.status === 'DEAD' || d.status === 'FAILED') && <button type="button" className="small-action" onClick={() => retry(d.id)}>Reenviar</button>}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
          <h3 className="card-title">Registro</h3>
          {history.length === 0 ? <p className="muted small">Sem registros.</p> : (
            <ul className="plain-list">{history.map((i, n) => (
              <li key={`${i.at}-${n}`}><b>{fmtDateTime(i.at)}</b> · {i.action}{i.detail ? ` — ${i.detail}` : ''}{i.ok === false ? ' (falha)' : ''}</li>
            ))}</ul>
          )}
        </div>
      )}
    </AsyncView>
  );
}

// ── IM-EXT-018-C01: catálogo operacional (dono: CERES CRM) × oferta analítica (dona: InteliMove) ───────────────────────
const KIND_TXT: Record<CatalogItemDto['kind'], string> = { PRODUCT: 'Produto', SERVICE: 'Serviço', PACKAGE: 'Pacote', OTHER: 'Outro' };
const STATUS_TXT: Record<CatalogItemDto['status'], string> = { ACTIVE: 'Ativo', INACTIVE: 'Inativo', RETIRED: 'Descontinuado' };
function CatalogSection({ slug, name, canManage }: { slug: string; name: string; canManage: boolean }) {
  const toast = useToast();
  const { state, reload } = useResource((s) => integrationsAdapter.catalog(slug, s), [slug]);
  const [data, setData] = useState<CatalogListDto | null>(null);
  useEffect(() => { if (state.kind === 'ready') setData(state.data); }, [state]);
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<CatalogListDto>, ok: string) => {
    setBusy(true);
    try { setData(await fn()); toast(ok); } catch (e) { toast(msg(e), 'error'); } finally { setBusy(false); }
  };
  const d = data;
  return (
    <section className="catalog" aria-labelledby={`cat-${slug}`}>
      <h3 className="card-title" id={`cat-${slug}`}>Catálogo operacional do {name}</h3>
      <p className="card-sub">O catálogo (produtos, serviços, pacotes, situação, código) é do {name} e aparece aqui só para leitura. As <b>ofertas analíticas</b> são da InteliMove: existem sem o {name}, alimentam contexto, ICP, segmentação, concorrência e TAM/SAM/SOM, e só se ligam a um item do catálogo por decisão de uma pessoa. Preço, margem, desconto e condição comercial não circulam.</p>
      {!d ? <AsyncView state={state} onRetry={reload} empty={null}>{() => null}</AsyncView> : <>
        {d.connection?.transport === 'SIMULATOR' && canManage && <SimulateCatalog slug={slug} disabled={busy || d.connection.status !== 'ACTIVE'} onDone={(x) => setData(x)} />}
        {d.items.length === 0 ? <p className="muted small">Nenhum item de catálogo recebido{d.connection?.transport === 'SIMULATOR' ? ' — use “Simular item do catálogo” para conferir o fluxo.' : '.'}</p> : (
          <div className="table-scroll"><table className="data-table catalog-table" style={{ minWidth: 0 }}>
            <caption className="sr-only">Itens do catálogo operacional</caption>
            <thead><tr><th scope="col">Item do catálogo ({name})</th><th scope="col">Versão</th><th scope="col">Oferta analítica (InteliMove)</th></tr></thead>
            <tbody>{d.items.map((it) => <CatalogRow key={it.externalId} it={it} d={d} slug={slug} canManage={canManage} busy={busy} act={act} />)}</tbody>
          </table></div>
        )}
      </>}
    </section>
  );
}

function CatalogRow({ it, d, slug, canManage, busy, act }: { it: CatalogItemDto; d: CatalogListDto; slug: string; canManage: boolean; busy: boolean; act: (fn: () => Promise<CatalogListDto>, ok: string) => Promise<void> }) {
  const free = d.offerings.filter((o) => !o.linkedExternalId);
  const [pick, setPick] = useState(free[0]?.id ?? '');
  const [just, setJust] = useState('');
  const l = it.link;
  return (
    <tr>
      <td data-label="Item"><b>{it.name}</b>{it.code && <span className="cell-sub"> · {it.code}</span>}
        <span className="cell-sub">{KIND_TXT[it.kind]} · {STATUS_TXT[it.status]} · dono: {it.sourceSystem === 'CERES_CRM' ? 'CERES CRM' : it.sourceSystem}{it.simulated ? ' (simulado no ambiente de validação)' : ''}</span>
        {it.description && <span className="cell-sub">{it.description}</span>}</td>
      <td data-label="Versão">v{it.version}<span className="cell-sub">{fmtDateTime(it.receivedAt)}</span></td>
      <td data-label="Oferta analítica">
        {l ? <>
          <b>{l.offeringName}</b> <StatusPill tone={l.state === 'CONFLICT' ? 'amber' : 'mint'}>{l.state === 'CONFLICT' ? 'Mudou no catálogo — decidir' : `Vinculada (v${l.acceptedVersion ?? '—'} revisada)`}</StatusPill>
          {l.state === 'CONFLICT' && <div className="catalog-conflict" role="group" aria-label={`Conflito em ${it.name}`}>
            <p className="cell-sub">{l.conflictReason} A oferta analítica não foi alterada.</p>
            <ul className="plain-list">{l.diff.map((x) => <li key={x.field}><b>{x.field === 'name' ? 'Nome' : 'Descrição'}</b> — InteliMove: “{x.offering ?? '—'}” · catálogo: “{x.external ?? '—'}”</li>)}</ul>
            {canManage && <>
              <div className="field"><label htmlFor={`cj-${it.externalId}`}>Justificativa (obrigatória)</label><input id={`cj-${it.externalId}`} value={just} maxLength={1000} onChange={(e) => setJust(e.target.value)} /></div>
              <div className="form-actions">
                <button type="button" className="secondary" disabled={busy || just.trim().length < 3} onClick={() => void act(() => integrationsAdapter.resolveCatalog(slug, it.externalId, { offeringId: l.offeringId, decision: 'KEEP_CURATED', justification: just.trim() }), 'Curadoria da InteliMove mantida; versão do catálogo registrada como revisada.')}>Manter a da InteliMove</button>
                <button type="button" className="cta" disabled={busy || just.trim().length < 3} onClick={() => void act(() => integrationsAdapter.resolveCatalog(slug, it.externalId, { offeringId: l.offeringId, decision: 'APPLY_EXTERNAL', fields: l.diff.map((x) => x.field), justification: just.trim() }), 'Campos do catálogo aplicados à oferta analítica.')}>Aplicar o do catálogo</button>
              </div>
            </>}
          </div>}
          {l.state === 'SYNCED' && l.diff.length > 0 && <span className="cell-sub">Diferenças conhecidas e aceitas no vínculo: {l.diff.map((x) => (x.field === 'name' ? 'nome' : 'descrição')).join(' e ')}.</span>}
          {canManage && <div><button type="button" className="small-action" disabled={busy} onClick={() => void act(() => integrationsAdapter.unlinkCatalog(slug, it.externalId), 'Vínculo desfeito; nada foi apagado.')}>Desvincular</button></div>}
        </> : canManage ? <div className="catalog-link">
          <span className="cell-sub">Sem oferta analítica vinculada.</span>
          {free.length > 0 && <><label className="sr-only" htmlFor={`cl-${it.externalId}`}>Oferta analítica para {it.name}</label>
            <select id={`cl-${it.externalId}`} value={pick} onChange={(e) => setPick(e.target.value)}>{free.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
            <button type="button" className="small-action" disabled={busy || !pick} onClick={() => void act(() => integrationsAdapter.linkCatalog(slug, it.externalId, pick), 'Oferta analítica vinculada ao item do catálogo.')}>Vincular</button></>}
          <button type="button" className="small-action" disabled={busy} onClick={() => void act(() => integrationsAdapter.offeringFromCatalog(slug, it.externalId), 'Oferta analítica criada a partir do catálogo e vinculada.')}>Criar oferta analítica</button>
        </div> : <span className="cell-sub">Sem oferta analítica vinculada.</span>}
      </td>
    </tr>
  );
}

function SimulateCatalog({ slug, disabled, onDone }: { slug: string; disabled: boolean; onDone: (d: CatalogListDto) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? '').trim() || undefined;
    setBusy(true);
    try { const r = await integrationsAdapter.simulateCatalog(slug, { externalId: v('externalId'), name: v('name'), code: v('code'), kind: v('kind') as 'SERVICE', description: v('description') }); onDone(r.catalog); toast(`Simulador publicou o item (situação: ${String(r.result.status ?? '—')}). Nada foi enviado ao CERES CRM.`); }
    catch (err) { toast(msg(err), 'error'); } finally { setBusy(false); }
  };
  return (
    <details className="details">
      <summary>Simular item do catálogo (ambiente de validação)</summary>
      <form onSubmit={submit} className="form-grid">
        <p className="muted small wide">O simulador publica o item pelo mesmo caminho do webhook real (contrato 1.3, assinado). Reenviar o mesmo identificador com outro nome ou descrição simula uma atualização do catálogo.</p>
        <div className="field"><label htmlFor="sim-ext">Identificador no catálogo</label><input id="sim-ext" name="externalId" required maxLength={200} placeholder="SKU-001" /></div>
        <div className="field"><label htmlFor="sim-code">Código</label><input id="sim-code" name="code" maxLength={80} /></div>
        <div className="field"><label htmlFor="sim-name">Nome</label><input id="sim-name" name="name" required maxLength={200} /></div>
        <div className="field"><label htmlFor="sim-kind">Tipo</label><select id="sim-kind" name="kind" defaultValue="SERVICE"><option value="PRODUCT">Produto</option><option value="SERVICE">Serviço</option><option value="PACKAGE">Pacote</option><option value="OTHER">Outro</option></select></div>
        <div className="field wide"><label htmlFor="sim-desc">Descrição</label><input id="sim-desc" name="description" maxLength={2000} /></div>
        <div className="form-actions wide"><button type="submit" className="secondary" disabled={busy || disabled}>{busy ? 'Simulando…' : 'Simular item'}</button></div>
      </form>
    </details>
  );
}
